import { useEffect, useState } from "react";
import { Box, Stack, Alert, Snackbar } from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2";
import { ByzMuiDateTimePicker } from "src/components/core-components/mui/inputs/ByzMuiDateTimePicker";
import ByzMuiTypography from "src/components/core-components/mui/inputs/ByzMuiTypography";
import ByzMuiTextField from "src/components/core-components/mui/inputs/ByzMuiTextField";
import ByzMuiButton from "src/components/core-components/mui/buttons/ByzMuiButton";
import ByzMuiRadioButton from "src/components/core-components/mui/buttons/ByzMuiRadioButton";
import { useTranslate } from "src/locales";
import {
  tollnerSepsisService,
  TollnerSepsisFormData,
} from "src/api/nurse/tollnerSepsisService";
import ByzNurseSaveButton from "src/components/core-components/mui/buttons/ByzNurseButton/ByzNurseSaveButton";
import ByzNurseDeleteButton from "src/components/core-components/mui/buttons/ByzNurseButton/ByzNurseDeleteButton";
import ByzNursePrintButton2 from "src/components/core-components/mui/buttons/ByzNurseButton/ByzNursePrintButton2";

//interface klasörü?
interface TollnerSepsisProps {
  existingData?: {
    id?: number;
    date?: string | Date;
    createdBy?: string;
    skinColorChange?: string;
    peripheralCirculation?: string;
    hypotonia?: string;
    bradycardia?: string;
    apnea?: string;
    respiratoryDistress?: string;
    hepatomegaly?: string;
    gastrointestinalFinding?: string;
    leukocyteCount?: string;
    leftShift?: string;
    thrombocytopenia?: string;
    metabolicAcidosis?: string;
    totalScore?: number;
    sepsisStatus?: string;
  } | null;
  onSave?: ((formData: TollnerSepsisFormData | null) => Promise<void>) | null;
}

interface HTMLInputElementWithValue extends HTMLInputElement {
  value: string;
}

// skor datası için model. react tarafında model klasörü olyp olmadığını sormalıyım.
interface ScoreData {
  score: number;
  status: string;
}

function TollnerSepsis({
  existingData = null,
  onSave = null,
}: TollnerSepsisProps) {
  const { t } = useTranslate();
  const [date, setDate] = useState<Date | null>(new Date());
  const [createdBy, setCreatedBy] = useState<string>("Current User");
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const [totalScore, setTotalScore] = useState<number>(0);
  const [sepsisStatus, setSepsisStatus] = useState<string>("");

  const [alertOpen, setAlertOpen] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [alertSeverity, setAlertSeverity] = useState<
    "error" | "warning" | "info" | "success"
  >("success");

  useEffect(() => {
    if (existingData) {
      setDate(existingData.date ? new Date(existingData.date) : new Date());
      setCreatedBy(existingData.createdBy || "Dr. Ahmet Yılmaz");
      setTotalScore(existingData.totalScore || 0);
      setSepsisStatus(existingData.sepsisStatus || "");

      //burada skorlar direkt html selectörlerinden geliyor fakat server dan gelecek. simüle edebilmek için yapıldı.
    }
  }, [existingData]);

  useEffect(() => {
    setCreatedBy("Dr. Ahmet Yılmaz");
  }, []);

  const calculateScore = (): ScoreData => {
    let score = 0;

    const skinColorChangeElement = document.querySelector(
      'input[name="skinColorChange"]:checked'
    ) as HTMLInputElementWithValue | null;
    const skinColorChange = skinColorChangeElement?.value;

    const peripheralCirculationElement = document.querySelector(
      'input[name="peripheralCirculation"]:checked'
    ) as HTMLInputElementWithValue | null;
    const peripheralCirculation = peripheralCirculationElement?.value;

    const hypotoniaElement = document.querySelector(
      'input[name="hypotonia"]:checked'
    ) as HTMLInputElementWithValue | null;
    const hypotonia = hypotoniaElement?.value;

    const bradycardiaElement = document.querySelector(
      'input[name="bradycardia"]:checked'
    ) as HTMLInputElementWithValue | null;
    const bradycardia = bradycardiaElement?.value;

    const apneaElement = document.querySelector(
      'input[name="apnea"]:checked'
    ) as HTMLInputElementWithValue | null;
    const apnea = apneaElement?.value;

    const respiratoryDistressElement = document.querySelector(
      'input[name="respiratoryDistress"]:checked'
    ) as HTMLInputElementWithValue | null;
    const respiratoryDistress = respiratoryDistressElement?.value;

    const hepatomegalyElement = document.querySelector(
      'input[name="hepatomegaly"]:checked'
    ) as HTMLInputElementWithValue | null;
    const hepatomegaly = hepatomegalyElement?.value;

    const gastrointestinalFindingElement = document.querySelector(
      'input[name="gastrointestinalFinding"]:checked'
    ) as HTMLInputElementWithValue | null;
    const gastrointestinalFinding = gastrointestinalFindingElement?.value;

    const leukocyteCountElement = document.querySelector(
      'input[name="leukocyteCount"]:checked'
    ) as HTMLInputElementWithValue | null;
    const leukocyteCount = leukocyteCountElement?.value;

    const leftShiftElement = document.querySelector(
      'input[name="leftShift"]:checked'
    ) as HTMLInputElementWithValue | null;
    const leftShift = leftShiftElement?.value;

    const thrombocytopeniaElement = document.querySelector(
      'input[name="thrombocytopenia"]:checked'
    ) as HTMLInputElementWithValue | null;
    const thrombocytopenia = thrombocytopeniaElement?.value;

    const metabolicAcidosisElement = document.querySelector(
      'input[name="metabolicAcidosis"]:checked'
    ) as HTMLInputElementWithValue | null;
    const metabolicAcidosis = metabolicAcidosisElement?.value;

    if (skinColorChange === "Orta") score += 1;
    if (skinColorChange === "Belirgin") score += 2;

    if (peripheralCirculation === "Orta") score += 1;
    if (peripheralCirculation === "Belirgin") score += 2;

    if (hypotonia === "Orta") score += 1;
    if (hypotonia === "Belirgin") score += 2;

    if (bradycardia === "Var") score += 1;
    if (apnea === "Var") score += 1;
    if (respiratoryDistress === "Var") score += 1;
    if (hepatomegaly === "> 4cm") score += 1;
    if (gastrointestinalFinding === "Var") score += 1;

    if (leukocyteCount === "Lökositoz") score += 1;
    if (leukocyteCount === "Lökopeni") score += 2;

    if (leftShift === "Orta") score += 1;
    if (leftShift === "Belirgin") score += 2;

    if (thrombocytopenia === "Var") score += 1;

    if (metabolicAcidosis === ">7.2") score += 1;
    if (metabolicAcidosis === "<7.2") score += 2;

    setTotalScore(score);

    let status = "Sepsis Yok";
    if (score < 5) {
      status = "Sepsis Yok";
    } else if (score >= 5 && score <= 10) {
      status = "Sepsis Olasılığı";
    } else {
      status = "Sepsis Teşhisi";
    }

    setSepsisStatus(status);

    return {
      score,
      status,
    };
  };

  useEffect(() => {
    const radioButtons = document.querySelectorAll('input[type="radio"]');
    radioButtons.forEach((button) => {
      button.addEventListener("change", calculateScore);
    });

    return () => {
      radioButtons.forEach((button) => {
        button.removeEventListener("change", calculateScore);
      });
    };
  }, []);

  const collectFormData = (): TollnerSepsisFormData => {
    const scoreData = calculateScore();

    return {
      date: date,
      createdBy: createdBy,
      skinColorChange:
        (
          document.querySelector(
            'input[name="skinColorChange"]:checked'
          ) as HTMLInputElementWithValue
        )?.value || "Yok",
      peripheralCirculation:
        (
          document.querySelector(
            'input[name="peripheralCirculation"]:checked'
          ) as HTMLInputElementWithValue
        )?.value || "Yok",
      hypotonia:
        (
          document.querySelector(
            'input[name="hypotonia"]:checked'
          ) as HTMLInputElementWithValue
        )?.value || "Yok",
      bradycardia:
        (
          document.querySelector(
            'input[name="bradycardia"]:checked'
          ) as HTMLInputElementWithValue
        )?.value || "Yok",
      apnea:
        (
          document.querySelector(
            'input[name="apnea"]:checked'
          ) as HTMLInputElementWithValue
        )?.value || "Yok",
      respiratoryDistress:
        (
          document.querySelector(
            'input[name="respiratoryDistress"]:checked'
          ) as HTMLInputElementWithValue
        )?.value || "Yok",
      hepatomegaly:
        (
          document.querySelector(
            'input[name="hepatomegaly"]:checked'
          ) as HTMLInputElementWithValue
        )?.value || "Yok",
      gastrointestinalFinding:
        (
          document.querySelector(
            'input[name="gastrointestinalFinding"]:checked'
          ) as HTMLInputElementWithValue
        )?.value || "Yok",
      leukocyteCount:
        (
          document.querySelector(
            'input[name="leukocyteCount"]:checked'
          ) as HTMLInputElementWithValue
        )?.value || "Normal",
      leftShift:
        (
          document.querySelector(
            'input[name="leftShift"]:checked'
          ) as HTMLInputElementWithValue
        )?.value || "Yok",
      thrombocytopenia:
        (
          document.querySelector(
            'input[name="thrombocytopenia"]:checked'
          ) as HTMLInputElementWithValue
        )?.value || "Yok",
      metabolicAcidosis:
        (
          document.querySelector(
            'input[name="metabolicAcidosis"]:checked'
          ) as HTMLInputElementWithValue
        )?.value || "Yok",
      totalScore: scoreData.score,
      sepsisStatus: scoreData.status,
    };
  };

  const handleSaveMedcard = async () => {
    try {
      setIsSubmitting(true);
      const formData = collectFormData();

      if (existingData?.id) {
        await tollnerSepsisService.updateTollnerSepsis(
          existingData.id,
          formData
        );
        setAlertMessage("Tıbbi kart başarıyla güncellendi");
      } else {
        await tollnerSepsisService.createTollnerSepsis(formData);
        setAlertMessage("Tıbbi kart başarıyla kaydedildi");
      }

      setAlertSeverity("success");
      setAlertOpen(true);

      if (onSave) {
        onSave(formData);
      }
    } catch (error) {
      console.error("Kaydetme hatası:", error);
      setAlertMessage("Tıbbi kart kaydedilirken hata oluştu!");
      setAlertSeverity("error");
      setAlertOpen(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteMedcard = async () => {
    try {
      setIsSubmitting(true);

      if (existingData?.id) {
        await tollnerSepsisService.deleteTollnerSepsis(existingData.id);
      }

      setAlertMessage("Tıbbi kart silindi");
      setAlertSeverity("success");
      setAlertOpen(true);

      if (onSave) {
        onSave(null);
      }
    } catch (error) {
      console.error("Silme hatası:", error);
      setAlertMessage("Tıbbi kart silinirken hata oluştu!");
      setAlertSeverity("error");
      setAlertOpen(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePrintMedcard = async () => {
    try {
      const formData = collectFormData();

      setAlertMessage("Tıbbi kart yazdırma işlemi başlatıldı");
      setAlertSeverity("success");
      setAlertOpen(true);
    } catch (error) {
      console.error("Yazdırma hatası:", error);
      setAlertMessage("Tıbbi kart yazdırılırken hata oluştu!");
      setAlertSeverity("error");
      setAlertOpen(true);
    }
  };

  const handleCloseAlert = () => {
    setAlertOpen(false);
  };

  const handleDateChange = (newDate: Date | null) => {
    setDate(newDate);
  };

  // dikey hizalama radio butonlar için
  const radioContainerStyle = {
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "center",
    width: "100%",
    height: "100%",
    padding: "10px 10px",
  };

  return (
    <>
      <Stack
        sx={{ padding: "5px", marginBottom: "20px" }}
        mt={5}
        direction="row"
        spacing={2}
      >
        <ByzNurseSaveButton
          onClick={handleSaveMedcard}
          disabled={isSubmitting}
        />

        <ByzNurseDeleteButton
          onClick={handleDeleteMedcard}
          disabled={isSubmitting || !existingData?.id}
        />

        <ByzNursePrintButton2
          onClick={handlePrintMedcard}
          disabled={isSubmitting}
        />
      </Stack>

      <Snackbar
        open={alertOpen}
        autoHideDuration={6000}
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseAlert}
          severity={alertSeverity}
          sx={{ width: "100%" }}
        >
          {alertMessage}
        </Alert>
      </Snackbar>

      <Box sx={{ padding: "20px", width: "100%" }}>
        <Grid container spacing={2}>
          <Grid md={9} xs={9} container direction={"column"} spacing={3}>
            {/* Form Header */}
            <Grid
              md={12}
              xs={12}
              sx={{
                backgroundColor: "rgb(231, 239, 252)",
                borderBottom: "1px solid #E0E0E0",
              }}
            >
              <ByzMuiTypography isTitle>
                {t("tollner-sepsis.tollner-sepsis-form")}
              </ByzMuiTypography>
            </Grid>

            {/* Date Time Section */}
            <Grid container alignItems="center" justifyContent="space-between">
              <Grid
                md={3}
                xs={4}
                sx={{
                  borderBottom: "1px solid #E0E0E0",
                  paddingRight: "5px",
                  paddingLeft: "30px",
                }}
              >
                <ByzMuiTypography>{t("datetime")}</ByzMuiTypography>
              </Grid>
              <Grid md={9} xs={8} sx={{ paddingTop: "0px" }}>
                <ByzMuiDateTimePicker
                  sx={{ width: "100%", maxWidth: "350px" }}
                  value={date}
                  onChange={handleDateChange}
                />
              </Grid>
            </Grid>

            {/* Created By */}
            <Grid
              container
              alignItems={"center"}
              justifyContent={"space-between"}
            >
              <Grid
                md={3}
                xs={4}
                sx={{
                  borderBottom: "1px solid #E0E0E0",
                  paddingRight: "5px",
                  paddingLeft: "30px",
                }}
              >
                <ByzMuiTypography>{t("createdby")}</ByzMuiTypography>
              </Grid>
              <Grid md={9} xs={8} sx={{ paddingTop: "0px" }}>
                <ByzMuiTextField
                  sx={{ width: "100%", maxWidth: "350px" }}
                  color="info"
                  disabled
                  value={createdBy}
                  label={t("createdby")}
                  variant="outlined"
                />
              </Grid>
            </Grid>

            {/* Section Header */}
            <Grid
              container
              alignItems={"center"}
              justifyContent={"space-between"}
            >
              <Grid
                md={12}
                xs={12}
                sx={{
                  backgroundColor: "rgb(231, 239, 252)",
                  borderBottom: "1px solid #E0E0E0",
                }}
              >
                <ByzMuiTypography isTitle>
                  {t("tollner-sepsis.tollner-sepsis")}
                </ByzMuiTypography>
              </Grid>
            </Grid>

            {/* Skin Color Change */}
            <Grid container alignItems="center" justifyContent="space-between">
              <Grid
                md={3}
                xs={4}
                sx={{
                  borderBottom: "1px solid #E0E0E0",
                  paddingRight: "5px",
                  paddingLeft: "30px",
                }}
              >
                <ByzMuiTypography>
                  {t("tollner-sepsis.altered-skin-color")}
                </ByzMuiTypography>
              </Grid>
              <Grid md={9} xs={8} sx={radioContainerStyle}>
                <ByzMuiRadioButton
                  typographyText="none,medium,evident"
                  layout="row"
                  name="skinColorChange"
                />
              </Grid>
            </Grid>

            {/* Peripheral Circulation */}
            <Grid container alignItems="center" justifyContent="space-between">
              <Grid
                md={3}
                xs={4}
                sx={{
                  borderBottom: "1px solid #E0E0E0",
                  paddingRight: "5px",
                  paddingLeft: "30px",
                }}
              >
                <ByzMuiTypography>
                  {t("tollner-sepsis.peripheral-circulation")}
                </ByzMuiTypography>
              </Grid>
              <Grid md={9} xs={8} sx={radioContainerStyle}>
                <ByzMuiRadioButton
                  typographyText="none,medium,evident"
                  layout="row"
                  name="peripheralCirculation"
                />
              </Grid>
            </Grid>

            {/* Hypotonia */}
            <Grid container alignItems="center" justifyContent="space-between">
              <Grid
                md={3}
                xs={4}
                sx={{
                  borderBottom: "1px solid #E0E0E0",
                  paddingRight: "5px",
                  paddingLeft: "30px",
                }}
              >
                <ByzMuiTypography>
                  {t("tollner-sepsis.hypotonia")}
                </ByzMuiTypography>
              </Grid>
              <Grid md={9} xs={8} sx={radioContainerStyle}>
                  <ByzMuiRadioButton
                    typographyText="none,medium,evident"
                    layout="row"
                    name="hypotonia"
                  />
              </Grid>
            </Grid>

            {/* Bradycardia */}
            <Grid container alignItems="center" justifyContent="space-between">
              <Grid
                md={3}
                xs={4}
                sx={{
                  borderBottom: "1px solid #E0E0E0",
                  paddingRight: "5px",
                  paddingLeft: "30px",
                }}
              >
                <ByzMuiTypography>
                  {t("tollner-sepsis.bradycardia")}
                </ByzMuiTypography>
              </Grid>
              <Grid md={9} xs={8} sx={radioContainerStyle}>
                  <ByzMuiRadioButton
                    typographyText="exists,not-exists"
                    layout="row"
                    name="bradycardia"
                  />
              </Grid>
            </Grid>

            {/* Apnea */}
            <Grid container alignItems="center" justifyContent="space-between">
              <Grid
                md={3}
                xs={4}
                sx={{
                  borderBottom: "1px solid #E0E0E0",
                  paddingRight: "5px",
                  paddingLeft: "30px",
                }}
              >
                <ByzMuiTypography>{t("tollner-sepsis.apnea")}</ByzMuiTypography>
              </Grid>
              <Grid md={9} xs={8} sx={radioContainerStyle}>
                  <ByzMuiRadioButton
                    typographyText="exists,not-exists"
                    layout="row"
                    name="apnea"
                  />
              </Grid>
            </Grid>

            {/* Respiratory Distress */}
            <Grid container alignItems="center" justifyContent="space-between">
              <Grid
                md={3}
                xs={4}
                sx={{
                  borderBottom: "1px solid #E0E0E0",
                  paddingRight: "5px",
                  paddingLeft: "30px",
                }}
              >
                <ByzMuiTypography>
                  {t("tollner-sepsis.respiratoryDistress")}
                </ByzMuiTypography>
              </Grid>
              <Grid md={9} xs={8} sx={radioContainerStyle}>
                  <ByzMuiRadioButton
                    typographyText="exists,not-exists"
                    layout="row"
                    name="respiratoryDistress"
                  />
              </Grid>
            </Grid>

            {/* Hepatomegaly */}
            <Grid container alignItems="center" justifyContent="space-between">
              <Grid
                md={3}
                xs={4}
                sx={{
                  borderBottom: "1px solid #E0E0E0",
                  paddingRight: "5px",
                  paddingLeft: "30px",
                }}
              >
                <ByzMuiTypography>
                  {t("tollner-sepsis.hepatomegaly")}
                </ByzMuiTypography>
              </Grid>
              <Grid md={9} xs={8} sx={radioContainerStyle}>
                  <ByzMuiRadioButton
                    typographyText="none,> 4cm"
                    layout="row"
                    name="hepatomegaly"
                  />
              </Grid>
            </Grid>

            {/* Gastrointestinal Finding */}
            <Grid container alignItems="center" justifyContent="space-between">
              <Grid
                md={3}
                xs={4}
                sx={{
                  borderBottom: "1px solid #E0E0E0",
                  paddingRight: "5px",
                  paddingLeft: "30px",
                }}
              >
                <ByzMuiTypography>
                  {t("tollner-sepsis.gastrointestinalFinding")}
                </ByzMuiTypography>
              </Grid>
              <Grid md={9} xs={8} sx={radioContainerStyle}>
                  <ByzMuiRadioButton
                    typographyText="exists,not-exists"
                    layout="row"
                    name="gastrointestinalFinding"
                  />
              </Grid>
            </Grid>

            {/* Leukocyte Count */}
            <Grid container alignItems="center" justifyContent="space-between">
              <Grid
                md={3}
                xs={4}
                sx={{
                  borderBottom: "1px solid #E0E0E0",
                  paddingRight: "5px",
                  paddingLeft: "30px",
                }}
              >
                <ByzMuiTypography>
                  {t("tollner-sepsis.leukocyteCount")}
                </ByzMuiTypography>
              </Grid>
              <Grid md={9} xs={8} sx={radioContainerStyle}>
                  <ByzMuiRadioButton
                    typographyText="normal,leukocytosis,leukopenia"
                    layout="row"
                    name="leukocyteCount"
                  />
              </Grid>
            </Grid>

            {/* Left Shift */}
            <Grid container alignItems="center" justifyContent="space-between">
              <Grid
                md={3}
                xs={4}
                sx={{
                  borderBottom: "1px solid #E0E0E0",
                  paddingRight: "5px",
                  paddingLeft: "30px",
                }}
              >
                <ByzMuiTypography>
                  {t("tollner-sepsis.leftShift")}
                </ByzMuiTypography>
              </Grid>
              <Grid md={9} xs={8} sx={radioContainerStyle}>
                  <ByzMuiRadioButton
                    typographyText="none,medium,evident"
                    layout="row"
                    name="leftShift"
                  />
              </Grid>
            </Grid>

            {/* Thrombocytopenia */}
            <Grid container alignItems="center" justifyContent="space-between">
              <Grid
                md={3}
                xs={4}
                sx={{
                  borderBottom: "1px solid #E0E0E0",
                  paddingRight: "5px",
                  paddingLeft: "30px",
                }}
              >
                <ByzMuiTypography>
                  {t("tollner-sepsis.thrombocytopenia")}
                </ByzMuiTypography>
              </Grid>
              <Grid md={9} xs={8} sx={radioContainerStyle}>
                  <ByzMuiRadioButton
                    typographyText="exists,not-exists"
                    layout="row"
                    name="thrombocytopenia"
                  />
              </Grid>
            </Grid>

            {/* Metabolic Acidosis */}
            <Grid container alignItems="center" justifyContent="space-between">
              <Grid
                md={3}
                xs={4}
                sx={{
                  borderBottom: "1px solid #E0E0E0",
                  paddingRight: "5px",
                  paddingLeft: "30px",
                }}
              >
                <ByzMuiTypography>
                  {t("tollner-sepsis.metabolicAcidosis")}
                </ByzMuiTypography>
              </Grid>
              <Grid md={9} xs={8} sx={radioContainerStyle}>
                  <ByzMuiRadioButton
                    typographyText="none,>7.2,<7.2"
                    layout="row"
                    name="metabolicAcidosis"
                  />
              </Grid>
            </Grid>

            {/* Other Section Header */}
            <Grid
              container
              alignItems={"center"}
              justifyContent={"space-between"}
            >
              <Grid
                md={12}
                xs={12}
                sx={{
                  backgroundColor: "rgb(231, 239, 252)",
                  borderBottom: "1px solid #E0E0E0",
                }}
              >
                <ByzMuiTypography isTitle>
                  {t("other")}
                </ByzMuiTypography>
              </Grid>
            </Grid>

            {/* Score */}
            <Grid
              container
              alignItems={"center"}
              justifyContent={"space-between"}
            >
              <Grid
                md={3}
                xs={4}
                sx={{
                  borderBottom: "1px solid #E0E0E0",
                  paddingRight: "5px",
                  paddingLeft: "30px",
                }}
              >
                <ByzMuiTypography>{t("score")}</ByzMuiTypography>
              </Grid>
              <Grid md={9} xs={8}>
                <ByzMuiTextField
                  sx={{ width: "100%", maxWidth: "350px" }}
                  color="info"
                  disabled
                  value={totalScore.toString()}
                  label={t("score")}
                  variant="outlined"
                />
              </Grid>
            </Grid>

            {/* Sepsis Status */}
            <Grid container alignItems="center" justifyContent="space-between">
              <Grid
                md={3}
                xs={4}
                sx={{
                  borderBottom: "1px solid #E0E0E0",
                  paddingRight: "5px",
                  paddingLeft: "30px",
                }}
              >
                <ByzMuiTypography>
                  {t("tollner-sepsis.sepsisStatus")}
                </ByzMuiTypography>
              </Grid>
              <Grid md={9} xs={8} sx={{ paddingTop: "0px" }}>
                <ByzMuiTextField
                  sx={{ width: "100%", maxWidth: "350px" }}
                  color="info"
                  disabled
                  value={sepsisStatus}
                  label={t("tollner-sepsis.sepsisStatus")}
                  variant="outlined"
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>

      {/* Calculate Score Button */}
      <ByzMuiButton
        variant="contained"
        color="primary"
        onClick={calculateScore}
        sx={{ marginTop: 2, marginLeft: 2, marginBottom: 2 }}
      >
        {t("tollner-sepsis.calculateScore")}
      </ByzMuiButton>
    </>
  );
}

export default TollnerSepsis;
